import * as React from 'react'
import spinners, { type BrailleSpinnerName } from 'unicode-animations'

const SPINNER_NAMES = [
  'braille',
  'braillewave',
  'dna',
  'scan',
  'rain',
  'pulse',
  'snake',
  'sparkle',
  'orbit',
  'breathe',
  'helix',
  'diagswipe',
] as const satisfies readonly BrailleSpinnerName[]

export interface BrailleSpinnerProps {
  /** Specific spinner name, or random if not specified */
  name?: string
  className?: string
}

function isBrailleSpinnerName(name: string): name is BrailleSpinnerName {
  return name in spinners
}

function pickRandomSpinnerName(): BrailleSpinnerName {
  return SPINNER_NAMES[Math.floor(Math.random() * SPINNER_NAMES.length)] ?? 'braille'
}

/**
 * Animated unicode braille spinner.
 * Cycles through braille character frames for a text-based loading animation.
 * Picks a random animation on mount for variety.
 */
export function BrailleSpinner({ name, className }: BrailleSpinnerProps) {
  const [randomSpinnerName, setRandomSpinnerName] = React.useState<BrailleSpinnerName>('braille')
  const [frameIndex, setFrameIndex] = React.useState(0)
  const requestedSpinnerName = name ?? ''

  // Pick a random spinner on mount, then rotate every 8 seconds for variety
  React.useEffect(() => {
    if (isBrailleSpinnerName(requestedSpinnerName)) return
    setRandomSpinnerName(pickRandomSpinnerName())

    const rotateInterval = setInterval(() => {
      setRandomSpinnerName(prev => {
        let next = pickRandomSpinnerName()
        while (next === prev && SPINNER_NAMES.length > 1) {
          next = pickRandomSpinnerName()
        }
        return next
      })
    }, 3000)
    return () => clearInterval(rotateInterval)
  }, [requestedSpinnerName])

  const spinnerName: BrailleSpinnerName = isBrailleSpinnerName(requestedSpinnerName)
    ? requestedSpinnerName
    : randomSpinnerName
  const spinner = spinners[spinnerName]

  React.useEffect(() => {
    setFrameIndex(0)

    const interval = setInterval(() => {
      setFrameIndex(prev => (prev + 1) % spinner.frames.length)
    }, spinner.interval)

    return () => clearInterval(interval)
  }, [spinner])

  return (
    <span className={className} role='status' aria-label='Loading'>
      {spinner.frames[frameIndex] ?? spinner.frames[0]}
    </span>
  )
}
